import { Type } from '@angular/core';

import * as am4charts from '@amcharts/amcharts4/charts';
import { AxisLabel, CategoryAxis, CategoryAxisDataItem, ColumnSeries, LabelBullet, Legend, ValueAxis, XYChart } from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';

import { stackedBarColorsConfig } from '../../../../Common/Charts/config/stacked-bar-colors.config';
import { LabelDisplayOptionsEnum } from '../../../Misc/enums/label-display-options.enum';
import { chartColors } from '../../enums/chart-colors.enum';
import { AbstractChartOptionsModel } from './../abstract-chart-options.model';
import { ChartMetadataModel } from './../chart-metadata.model';
import { ChartModel } from './../chart.model';
import { CustomLegendItem } from './../custom-legend-item.model';
import { DataPointModel } from './../data-point.model';
import { SeriesModel } from './../series.model';
import { StackedBarChartOptionModel } from './stacked-bar-chart-option.model';
import { StackedBarChartOptionsModel } from './stacked-bar-chart-options.model';
import { StackedColumnChartSeriesInfo } from './stacked-column-chart-series-info.model';
import { ChartSizeHelper } from '../../helpers/chart-size.helper';


export class HorizontalStackedBarChartModel extends ChartModel {
    readonly chartType: Type<XYChart> = XYChart;
    readonly singleSeries: boolean = false;
    readonly chartDisplayLimit: number = 12;
    readonly smallChartDisplayLimit: number = 3;
    declare amchart: XYChart;
    declare options: StackedBarChartOptionsModel;
    columns: ColumnSeries[] = [];
    totalSeries: ColumnSeries;
    seriesInfoMap: Map<string, StackedColumnChartSeriesInfo>;
    currentSeriesMap: Map<string, ColumnSeries>;
    flatData: DataPointModel[] = [];
    private labelTemplate: AxisLabel;
    private valueAxis: ValueAxis;
    private categoryAxis: CategoryAxis;
    private cachedFlatData: DataPointModel[];
    private colorMap: { [key: string]: string };
    private barClicked: (category: any) => void;

    constructor(
        series: SeriesModel[],
        options: AbstractChartOptionsModel = {},
        metadata?: ChartMetadataModel,
        colorMap?: { [key: string]: string },
        clickCallback?: (category: any) => void
    ) {
        super(series, options, metadata);
        this.colorMap = colorMap;
        this.barClicked = clickCallback;
    }

    public initChart(element: HTMLElement): void {
        super.initChart(element, this.chartType);

        this.seriesInfoMap = new Map<string, StackedColumnChartSeriesInfo>();
        this.currentSeriesMap = new Map<string, ColumnSeries>();
        this.amchart.maskBullets = true;
        this.amchart.paddingRight = 30;

        if (this.titleLabel) {
            this.titleLabel.tooltipText = this.options.titleTooltipText ?? this.metadata.titleText;
        }
        if (this.subtitleLabel) {
            this.subtitleLabel.tooltipText = this.options.subtitleTooltipText ?? this.metadata.subtitleText;
        }

        this.valueAxis = this.amchart.xAxes.push(new ValueAxis());
        this.valueAxis.renderer.grid.template.disabled = true;
        this.valueAxis.renderer.baseGrid.disabled = true;

        if (this.options.hundredPercentStacked) {
            this.valueAxis.renderer.line.strokeOpacity = 1;
            this.valueAxis.renderer.line.strokeWidth = 2;
            this.valueAxis.renderer.line.stroke = am4core.color('#ced4da');
            this.valueAxis.renderer.ticks.template.disabled = false;
            this.valueAxis.renderer.ticks.template.strokeOpacity = 1;
            this.valueAxis.renderer.ticks.template.stroke = am4core.color('#ced4da');
            this.valueAxis.renderer.ticks.template.strokeWidth = 1;
            this.valueAxis.renderer.ticks.template.length = 6;
            this.valueAxis.max = 100;
            this.valueAxis.renderer.minGridDistance = 10;
            this.valueAxis.renderer.labels.template.adapter.add('text', (text, target) => {
                return text + '%';
            });
            this.valueAxis.renderer.labels.template.adapter.add('dx', (text, target) => {
                const percentageValue = parseInt(target.currentText, 10);
                if (percentageValue === 0) {
                    return 10;
                } else if (percentageValue === 100) {
                    return -20;
                }
                return text;
            });
        } else {
            this.valueAxis.extraMax = 0.1;
            this.valueAxis.renderer.labels.template.disabled = true;
            this.valueAxis.paddingRight = this.options.includeTotalSeries ? 35 : 0;
        }
        if (this.options.imageAsCategory || this.options.customCategoryTemplate) {
            this.valueAxis.renderer.ticks.template.paddingLeft = 30;
            this.valueAxis.paddingLeft = 30;
        }

        this.valueAxis.min = 0;
        this.valueAxis.strictMinMax = true;
        this.valueAxis.calculateTotals = true;

        this.categoryAxis = this.amchart.yAxes.push(new CategoryAxis());
        this.categoryAxis.dataFields.category = 'label';
        this.categoryAxis.renderer.minGridDistance = 1;
        this.categoryAxis.isMeasured = false;
        this.categoryAxis.renderer.grid.template.disabled = true;

        if (this.options.hundredPercentStacked) {
            this.categoryAxis.renderer.line.strokeOpacity = 1;
            this.categoryAxis.renderer.line.strokeWidth = 2;
            this.categoryAxis.renderer.line.stroke = am4core.color('#ced4da');
            this.categoryAxis.startLocation = -0.15;
        }
        if (this.options.imageAsCategory) {
            this.categoryAxis.renderer.labels.template.disabled = true;
            this.categoryAxis.paddingLeft = 30;
            const image = new am4core.Image();
            image.width = 20;
            image.height = 20;
            image.verticalCenter = 'middle';
            image.dx = -25;
            image.tooltip = new am4core.Tooltip();
            image.tooltip.getFillFromObject = false;
            image.tooltip.pointerOrientation = 'left';
            image.tooltip.background.fill = am4core.color('#ffffff');
            image.tooltip.background.cornerRadius = 4;
            image.adapter.add('href', (href, target) => {
                const category = (target.dataItem as CategoryAxisDataItem).category;
                if (category) {
                    target.tooltipHTML = `
                        <div style="font-size: 0.875rem; color: #292929;">
                            ${category}
                        </div>
                    `;
                    return this.options.appBaseHref + 'assets/images/' + category.toLowerCase() + '_logo.png';
                }
                return href;
            });
            this.categoryAxis.dataItems.template.bullet = image;
        } else if (this.options.categoryLabelStackedOnTop || this.options.responsive) {
            // CATEGORY LABEL APPEARANCE
            this.labelTemplate = this.categoryAxis.renderer.labels.template;
            this.labelTemplate.align = 'left';
            this.labelTemplate.fontSize = '0.75rem';
            this.categoryAxis.renderer.cellStartLocation = 0.25;
            this.categoryAxis.renderer.cellEndLocation = 0.75;
            this.labelTemplate.paddingLeft = 0;
            this.labelTemplate.dy = -18;
        }

        // Create series for total to display the total to the right of columns
        if (this.options.includeTotalSeries) {
            this.totalSeries = new ColumnSeries();
            this.totalSeries.dataFields.valueX = 'NONE';
            this.totalSeries.dataFields.categoryY = 'label';
            this.totalSeries.stacked = true;
            this.totalSeries.hiddenInLegend = true;
            this.totalSeries.columns.template.strokeOpacity = 0;
            const totalBullet = this.totalSeries.bullets.push(new LabelBullet());
            totalBullet.label.text = '[bold]{valueX.total}';
            totalBullet.label.dx = 20;
            totalBullet.label.hideOversized = false;
            totalBullet.label.fontSize = '0.75rem';
            totalBullet.label.truncate = false;
        }

        if (this.options.includeScrollbarX) {
            this.amchart.scrollbarX = new am4core.Scrollbar();
            this.amchart.scrollbarX.dx = 10;
            this.amchart.scrollbarX.dy = -12;
            this.amchart.scrollbarX.scale = 0.9;
            this.amchart.zoomOutButton.disabled = false;
        }

        if (this.options.legendContainerElement) {
            this.initCustomLegend();
        } else {
            // Set up the legend
            this.amchart.legend = new Legend();
            this.amchart.legend.position = 'bottom';
            this.amchart.legend.align = 'center';
            this.amchart.legend.contentAlign = 'center';
            this.amchart.legend.fontSize = '0.688rem';
            this.amchart.legend.marginTop = 10;
            this.amchart.legend.marginBottom = 10;

            this.amchart.legend.labels.template.text = '{name}';
            this.amchart.legend.labels.template.truncate = false;
            this.amchart.legend.markers.template.scale = 0.75;

            this.amchart.legend.itemContainers.template.width = 100;
            this.amchart.legend.itemContainers.template.paddingTop = 2;
            this.amchart.legend.itemContainers.template.paddingBottom = 2;
            this.amchart.legend.itemContainers.template.paddingLeft = 14;
        }

        // set up chart series key and options
        this.options?.seriesIds?.forEach((id, index) => {
            this.addSeriesKey(
                id,
                {
                    label: this.options.seriesLabels[index],
                    color: this.styles?.colorMap[id] || stackedBarColorsConfig[id],
                    labelColor: this.styles?.colorMap[id + '_LABEL'] || chartColors.agprWhite
                }
            );
        });

        // RESPONSIVE
        if (this.options.responsive) {
            this.amchart.responsive.enabled = true;

            // in shared report, the far right is nav button, so give some padding to zoomOutButton
            this.amchart.zoomOutButton.dx = -40;

            // Mid to large screen
            this.amchart.responsive.rules.push(
                {
                    id: 'large',
                    relevant: (target) => target.pixelWidth > 768,
                    state: function (target, stateId) {
                        if (target instanceof am4charts.AxisLabel) {
                            const labelState = target.states.create(stateId);
                            labelState.properties.fontSize = 16;
                            return labelState;
                        }
                    }
                }
            );

            // Small screen
            this.amchart.responsive.rules.push(
                {
                    id: 'small',
                    relevant: (target) => {
                        return target.pixelWidth <= 768;
                    },
                    state: function (target, stateId) {
                        if (target instanceof am4charts.AxisLabel) {
                            const labelState = target.states.create(stateId);
                            labelState.properties.fontSize = 14;
                            return labelState;
                        }
                    }
                }
            );
        } else {
            const smChartWidth = ChartSizeHelper.getMinWidthAtMinCanvas();
            const smChartHeight = ChartSizeHelper.getHeight(3, 1);
            const xsChartHeight = ChartSizeHelper.getHeight(2, 1);
            this.amchart.events.on('sizechanged', (event) => {
                const chart = event.target;

                if (this.labelTemplate) {
                    this.labelTemplate.dy = -this.amchart.series[0]?.values[0]?.height / 2 || 0;
                }

                if (this.flatData?.length) {
                    if (chart.pixelHeight <= xsChartHeight) {
                        this.amchart.data = this.flatData.slice(0, this.smallChartDisplayLimit);
                    } else {
                        this.amchart.data = this.flatData.slice(0, this.chartDisplayLimit);
                    }
                }

                if (this.amchart.scrollbarX) {
                    if (chart.pixelHeight <= smChartHeight) {
                        this.amchart.scrollbarX.disabled = true;
                    } else {
                        this.amchart.scrollbarX.disabled = false;
                    }
                }
            });
        }
    }

    getCustomLegendItems(): CustomLegendItem[] {
        const items: CustomLegendItem[] = [];

        this.seriesInfoMap?.forEach((info, key) => {
            if (!info) {
                return;
            }
            // TODO: show the tooltips for each column
            // Currently only one will show at a time
            // const columns = this.columns.find(c => c.dataFields.valueY === key)?.columns;

            const item: CustomLegendItem = {
                label: info.label,
                color: info.color,
                // SEE TODO above
                // mouseover: () => {
                //     columns?.each(sliver => sliver.showTooltip());
                // },
                // mouseout: () => {
                //     columns?.each(sliver => sliver.hideTooltip());
                // },
            };
            items.push(item);
        });

        return items;
    }

    /**
     * Updates the current presentation of data in the chart
     */
    public updateData(data: DataPointModel[][]): void {
        this.rawData = data;

        // after the chart is validated, it calculates height of column, and adjust label accordingly
        this.amchart.events.on('validated', event => {
            setTimeout(() => {
                if ((this.options.categoryLabelStackedOnTop || this.options.responsive)
                        && this.columns && this.columns.length > 0 && this.columns[0].columns.values[0]
                ) {
                    this.labelTemplate.dy = -this.columns[0].columns.values[0].height / 2 - 9;

                }
            }, 100);
        });

        // @ HACK validated hook is not always reliable, and in some extreme cases, it fails to update the label position
        this.amchart.events.on('ready', event => {
            setTimeout(() => {
                if ((this.options.categoryLabelStackedOnTop || this.options.responsive)
                        && this.columns && this.columns.length > 0 && this.columns[0].columns.values[0]
                ) {
                    this.labelTemplate.dy = -this.columns[0].columns.values[0].height / 2 - 9;
                }
            }, 100);
        });

        let flatData: DataPointModel[] = [];
        for (let i = 0; i < data.length; i++) {
            const seriesData = data[i];

            if (seriesData) {
                // extract unique sources
                const labels = [...new Set(seriesData.map(item => item.source))];
                labels.forEach(label => {
                    const dataPoint: DataPointModel = {
                        label: label as string,
                        // NONE is used for totalSeries
                        NONE: 0
                    };
                    let total = 0;
                    seriesData.filter(dataItem => {
                        if (dataItem.source === label) {
                            total += +dataItem.yValue;
                            return true;
                        }
                    }).forEach(dataItem => {
                        dataPoint[dataItem.id] = dataItem.yValue;
                        if (this.options.seriesIds?.length > 1) {
                            // if the chart has two or more column series
                            // calculate the percent value for each column series for csv export
                            dataItem.percent = +(dataItem.yValue / total).toFixed(3);
                            dataPoint[`${dataItem.id}.percent`] = dataItem.percent;
                            dataPoint[`${dataItem.id}.percentDisplayString`] = (!isNaN(dataItem.percent) ? dataItem.percent * 100 + '%' : '0%');
                            dataPoint[`${dataItem.id}.color`] = this.styles.colorMap[dataItem.id];
                            dataPoint[`${dataItem.id}.viewMentionUrl`] = dataItem.viewMentionUrl;
                        }
                    });

                    flatData.push(dataPoint);
                });
            }
        }

        if (this.options.sortValues) {
            // sort the data so that bar ranks from most to least
            flatData.sort((a, b) => {
                let aTotal = 0;
                let bTotal = 0;
                for (const key of this.seriesInfoMap.keys()) {
                    if (!isNaN(+a[key])) {
                        aTotal += +a[key];
                    }

                    if (!isNaN(+b[key])) {
                        bTotal += +b[key];
                    }
                }
                return aTotal - bTotal;
            });
        }

        this.cachedFlatData = flatData.slice();

        // only show the top 10 sources
        if (flatData.length > this.chartDisplayLimit) {
            flatData = flatData.slice(flatData.length - this.chartDisplayLimit);
        }

        this.flatData = flatData;
        this.amchart.data = JSON.parse(JSON.stringify(flatData));

        for (const [key, value] of this.seriesInfoMap.entries()) {
            if (this.currentSeriesMap.get(key) === undefined) {
                this.currentSeriesMap.set(key, this.addSeries(key, value));
            }
        }

        // add total series, which is used to calculate total number and display it at the end of bar
        if (this.options.includeTotalSeries && !this.amchart.series.contains(this.totalSeries)) {
            this.amchart.series.push(this.totalSeries);
        }

        if (
            !data?.includes(null) &&
            this.isContentEmpty()
        ) {
            if (this.amchart.legend) {
                this.amchart.legend.disabled = true;
            }
            this.valueAxis.disabled = true;
            this.categoryAxis.disabled = true;
            this.amchart.scrollbarX = null;
            this.amchart.zoomOutButton.disabled = true;
            this.showNoContentMessage();
            this.noContentLabel.dom.style.display = 'block';
        } else {
            this.valueAxis.disabled = false;
            this.categoryAxis.disabled = false;
            this.amchart.zoomOutButton.disabled = false;
            if (this.noContentLabel) {
                this.noContentLabel.dom.style.display = 'none';
                this.noContentLabel.text = '';
            }
        }

        this.updateCustomLegend();

        this.ready.next(true);
    }

    private addSeriesKey(key: string, label: StackedColumnChartSeriesInfo): void {
        if (this.seriesInfoMap.get(key) === undefined) {
            this.seriesInfoMap.set(key, label);
        }
    }

    private addSeries(seriesIdentifier: string, seriesInfo: StackedColumnChartSeriesInfo): ColumnSeries {
        const amseries = new ColumnSeries();
        const columnTemplate = amseries.columns.template;

        // if it only has one or two bars, this makes it not too high
        if (this.amchart.data.length <= 2) {
            if (this.options.adjustedBarHeightPercent) {
                amseries.columns.template.height = am4core.percent(this.options.adjustedBarHeightPercent);
            }
            if (this.options.imageAsCategory) {
                const imageBullet = this.categoryAxis.dataItems.template.bullet;
                imageBullet.width = 20;
                imageBullet.height = 20;
                imageBullet.dx = -26;
            }
        }

        if (this.options.seriesIds.length === 1) {
            // if it only has on column series, add border raduis to all corners
            columnTemplate.column.cornerRadius(2, 2, 2, 2);
        } else {
            this.options.seriesIds.forEach((id, index) => {

                // if it is the first column series, add border raduis to top-left and bottom-left
                if (id === seriesIdentifier && index === 0) {
                    columnTemplate.column.cornerRadius(2, 0, 2, 0);
                }

                // if it is the last column series, add border raduis to top-right and bottom-right
                if (id === seriesIdentifier && index === (this.options.seriesIds.length - 1)) {
                    columnTemplate.column.cornerRadius(0, 2, 0, 2);
                }
            });
        }

        // make some space between bars for the category label
        if (this.options.categoryLabelSatckedOnTop) {
            columnTemplate.column.paddingTop = 7;
            columnTemplate.column.paddingBottom = 7;
            amseries.columns.template.height = am4core.percent(40);
        }

        amseries.dataFields.valueX = seriesIdentifier;
        if (this.options.hundredPercentStacked) {
            amseries.dataFields.valueXShow = 'totalPercent';
        }
        if (this.options.imageAsCategory || this.options.customCategoryTemplate) {
            amseries.paddingLeft = 30;
        }
        if (this.options.hoverEffect) {
            columnTemplate.cursorOverStyle = am4core.MouseCursorStyle.pointer;

            columnTemplate.events.on('hit', event => {
                this.barClicked(event.target.dataItem.categories.categoryY);
            });
        }

        amseries.dataFields.categoryY = 'label';
        amseries.stacked = true;
        amseries.dataItems.template.locations.categoryY = 0.5;

        // set tooltip style
        amseries.tooltip.getFillFromObject = false;
        amseries.tooltip.background.fill = am4core.color(chartColors.agprWhite);
        amseries.tooltip.background.stroke = am4core.color('#ced4da');
        amseries.tooltip.background.cornerRadius = 8;
        amseries.tooltip.background.fillOpacity = 1;
        // clear tooltip background shadow
        amseries.tooltip.background.filters.clear();
        amseries.tooltip.label.padding(5, 10, 5, 10);
        amseries.tooltip.label.fill = am4core.color(chartColors.agprBlack);
        amseries.tooltip.label.interactionsEnabled = true;
        amseries.tooltip.label.maxWidth = 300;
        amseries.tooltip.label.wrap = true;
        amseries.tooltip.keepTargetHover = true;
        amseries.columns.template.tooltipText = this.options.tooltipText;
        if (this.options.tooltipHTML) {
            columnTemplate.tooltipHTML = this.options.tooltipHTML;
            columnTemplate.adapter.add('tooltipHTML', (text, target) => {
                if (target?.dataItem?.dataContext
                    && target.dataItem.dataContext['label'] !== 'noData'
                    && (this.options as StackedBarChartOptionsModel).tooltipHTML) {
                    return (this.options as StackedBarChartOptionsModel).tooltipHTML
                        .replace('{viewMentionUrl}',  target.dataItem.dataContext[`${seriesIdentifier}.viewMentionUrl`])
                        .replace('{color}', target.dataItem.dataContext[`${seriesIdentifier}.color`]);
                }
                return '';
            });
        }


        amseries.name = seriesInfo.label;
        amseries.fill = am4core.color(seriesInfo.color);

        const bullets = amseries.bullets.push(new LabelBullet());
        bullets.interactionsEnabled = false;
        bullets.label.truncate = false;
        bullets.label.hideOversized = true;
        bullets.label.fill = am4core.color(seriesInfo.labelColor);

        bullets.label.text = '';
        if (this.metadata?.labelDisplayOptions) {

            const doCount: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.count);
            const doPercent: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.percent);

            if (doCount) {
                bullets.label.text += '[font-style:italic]{valueX}[/]';
            }

            if (doCount && doPercent) {
                bullets.label.text += ', ';
            }

            if (doPercent) {
                bullets.label.text += '{valueX.totalPercent.formatNumber(\'#.0\')}%';
            }

        // this is necessary to reset the value in some situations
        } else {
            bullets.label.text = '';
        }

        // this means it is a non-stacked bar chart but a normal bar chart, we don't display the label
        if (this.seriesInfoMap.size <= 1) {
            bullets.label.text = '';
        }

        bullets.label.fontSize = 10;
        bullets.label.fontWeight = this.options.bulletLabelFontWeight;
        bullets.locationX = 0.5;
        if (this.options.imageAsCategory || this.options.customCategoryTemplate) {
            bullets.paddingLeft = 30;
        }

        // Create custom category label
        if (this.options.customCategoryTemplate) {
            this.labelTemplate = this.categoryAxis.renderer.labels.template;
            this.labelTemplate.padding(0, 0, 0, 0);
            this.labelTemplate.dx = 15;
            this.categoryAxis.dx = -20;
            this.labelTemplate.tooltip = new am4core.Tooltip();
            this.labelTemplate.tooltip.getFillFromObject = false;
            this.labelTemplate.tooltip.pointerOrientation = 'left';
            this.labelTemplate.tooltip.background.fill = am4core.color('#ffffff');
            this.labelTemplate.tooltip.background.cornerRadius = 4;
            this.labelTemplate.tooltip.label.wrap = true;
            this.labelTemplate.adapter.add('html', (value, target) => {
                if (target.dataItem?.dataContext && target.dataItem.dataContext['label']) {
                    const name = target.dataItem.dataContext['label'];
                    const nameSplit = name.trim().split(' ');
                    let labelText;
                    if (nameSplit.length === 1) {
                        labelText = name.slice(0, Math.min(nameSplit[0].length, 2)).toLocaleUpperCase();
                    } else {
                    // use the first char of the first and last word
                        labelText = nameSplit[0][0].toLocaleUpperCase() + nameSplit[nameSplit.length - 1][0].toLocaleUpperCase();
                    }
                    target.tooltipHTML = `
                    <div style="font-size: 0.875rem; color: #292929; max-width: 350px; overflow-wrap: break-word;">
                        ${name}
                    </div>
                `;
                    return this.options.customCategoryTemplate.replace('{category}', labelText);
                }
            });

        }

        this.amchart.series.push(amseries);
        this.columns.push(amseries);

        return amseries;
    }

    public updateSeriesStyles(): void {}

    /**
     * Modifies the chart to have the right styles and be the right dimensions for export
     */
    public setExportStyles(scale?: number, keepFontsize?: boolean): void {
        super.setExportStyles(scale, keepFontsize);
        if (this.amchart.scrollbarX) {
            this.amchart.scrollbarX.visible = false;
        }
        this.amchart.legend.fontSize = 14;
        this.amchart.legend.markers.template.scale = 1;
        this.amchart.legend.itemContainers.template.width = 120;

        if (this.options.categoryLabelStackedOnTop) {
            this.labelTemplate.fontSize = 14;
            // fixes an issue where bars & labels overlap in the export with datasets smaller than 10 items
            this.labelTemplate.paddingBottom = 12 + Math.max(0, (10 - this.amchart.data.length) * 3);
        }
    }

    /**
     * Undoes the changes made in setExportStyles to make the chart normal again
     */
    public unsetExportStyles(): void {
        this.labelTemplate.paddingBottom = 8;
        this.amchart.legend.itemContainers.template.width = 100;
        this.amchart.legend.markers.template.scale = 0.75;
        this.amchart.legend.fontSize = 11;
        this.labelTemplate.fontSize = 12;
        if (this.amchart.scrollbarX) {
            this.amchart.scrollbarX.visible = true;
        }

        super.unsetExportStyles();
    }

    /**
     * Exports a chart via amcharts
     */
    public export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {
        const dataFields = {
            label: this.options.label
        };
        const data = this.rawData[0];

        // Pulls data from ReportsChartOptionsService.mediaTypeBreakDownComparisonOptions
        data.forEach(dataPoint => {
            const option: StackedBarChartOptionModel = (this.options[`${dataPoint.id}`] as StackedBarChartOptionModel);
            dataFields[dataPoint.id] = option ? option.countLabel : undefined;
        });

        // looped over twice because order of headers is important for csv
        data.forEach(dataPoint => {
            const option: StackedBarChartOptionModel = (this.options[`${dataPoint.id}`] as StackedBarChartOptionModel);
            dataFields[`${dataPoint.id}.percentDisplayString`] = option ? option.percentLabel : undefined;
        });

        let flatData = this.cachedFlatData.slice();

        // For csv and json export, we want to export all data instead of only top ten sources
        // @Hack I tried to use this.amchart.clone() and am4core.object.clone(), but both failed with errors
        // it seems amcharts does not do deep clone, so here we use existing amchart, and pass all data to get export result
        // after that, reset the data to top 10 sources to display
        if (type === 'csv' || type === 'json' ) {
            this.amchart.data = JSON.parse(JSON.stringify(flatData.slice().reverse()));
        }

        this.amchart.exporting.dataFields = dataFields;
        this.amchart.exporting.filePrefix = filename;
        this.amchart.exporting.useWebFonts = false;

        const res = this.amchart.exporting.export(type);

        // put the top 10 sources back to display after exporting csv and json files
        if (type === 'csv' || type === 'json') {
            if (flatData.length > this.chartDisplayLimit) {
                flatData = flatData.slice(flatData.length - this.chartDisplayLimit);
            }

            this.amchart.data = JSON.parse(JSON.stringify(flatData));
        }

        return res;
    }

    public isContentEmpty(): boolean {
        if (!this.rawData) {
            return true;
        }
        for (const series of this.rawData) {
            if (!series) {
                continue;
            }
            for (const dataPoint of series) {
                if (dataPoint.yValue > 0) {
                    return false;
                }
            }
        }
        return true;
    }

    public clone(): HorizontalStackedBarChartModel {
        return new HorizontalStackedBarChartModel(
            this.series.map(s => s.clone()),
            this.options,
            this.metadata
        );
    }
}
